import { FC, ReactNode, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import statisticContext from 'context/statisticsContext'
import Commentaries from 'components/parts/commentaries/commentaries'
import WriteUs from 'components/parts/write-us/write-us-popup/write-us'
import WriteUsBtn from 'components/parts/write-us/write-us-btn/write-us-btn'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import BreadcrumbsPage from 'components/parts/breadcrumb-page/breadcrumb-page'
import Playlist from '../article-videoproject/playlist/playlist'
import Loader from 'components/parts/loader/loader'
import VideoBlockProject from 'components/webinar/video-block-project/video-block-project'
import SwitchToggle from 'components/parts/switch-toggle/switch-toggle'
import usePCWindow from 'hooks/usePCWindow'

import { getAxiosSingle } from 'pages/api/axios'
import { ScreenSize } from 'constants/media-constanst'
import { URL_SITE } from 'constants/data'
import { IMaterialCard } from 'constants/types/card.types'
import { IMaterial } from 'constants/types/webinar.types'
import { SocketEvents } from 'constants/events'
import { BASE_URL } from 'constants/url'
import { getCurrVideoIndex } from '../article-videoproject/utils'
import { OPTIONS, SWITCH_DESCRIPTION } from '../article-videoproject/data'

import styles from './../articles.module.scss'
import stylesVideo from '../article-videoproject/article-videoproject.module.scss'

interface IArticleVideo {
  dataset: IMaterial
  isWebinar?: boolean
  isWithFormat?: boolean
  children?: ReactNode
}

const ArticleVideoWithPlaylist: FC<IArticleVideo> = (props: IArticleVideo) => {
  const [toggleFeedback, setToggleFeedback] = useState(false)
  const [selectedOption, setSelectedOption] = useState(OPTIONS[0])
  const router = useRouter()
  const { query } = router
  const { dopId, origin } = query
  const [playlist, setPlaylist] = useState<IMaterialCard[]>([])
  const { dataset, isWebinar, isWithFormat, children } = props

  const socket = useContext(statisticContext)
  const isBigPC = usePCWindow(ScreenSize.big)
  const isFullInShow = selectedOption === SWITCH_DESCRIPTION

  let currWebinarIndex = 0
  if (playlist?.length > 0) currWebinarIndex = getCurrVideoIndex(playlist, dataset.id)
  const isLoaded = playlist.length > 0

  const handleToggleFeedback = () => setToggleFeedback((prevValue) => !prevValue)

  useEffect(() => {
    if (isBigPC) setSelectedOption(SWITCH_DESCRIPTION)
  }, [isBigPC])

  useEffect(() => {
    if (isWebinar && socket?.sendEvent) socket?.sendEvent(SocketEvents.WEBINAR_RECORD)
  }, [socket])

  useEffect(() => {
    if (dataset.playlistId?.length) {
      const isFromVideoproject = Boolean(dopId) && origin === 'videoproject'
      const playlistId = isFromVideoproject
        ? dataset.playlistId.find((element) => element === Number(dopId))
        : dataset.playlistId[dataset.playlistId.length - 1]

      const getPlaylistData = async () => {
        const webinarsData = await getAxiosSingle(
          `${BASE_URL.WEBINAR}?playlistId=${playlistId}&limit=50`
        )
        const articlesData = await getAxiosSingle(
          `${BASE_URL.ARTICLE}?playlistId=${playlistId}&limit=50`
        )
        const currPlaylist = await getAxiosSingle(`${BASE_URL.VIDEOPROJECTS}/${playlistId}`)

        const webinarsResult = webinarsData.status === 404 ? [] : webinarsData
        const articlesResult = articlesData.status === 404 ? [] : articlesData

        const result = [...webinarsResult, ...articlesResult]
        const orderedResult = result
          .map((material) => {
            const { id: materialId, materialType } = material
            const playlistElem = currPlaylist.list.find(
              (video: any) => video.materialId === materialId && video.materialType === materialType
            )
            return { ...material, order: playlistElem?.index }
          })
          .sort((a, b) => a.order - b.order)

        setPlaylist(orderedResult)
      }
      getPlaylistData()
    }
  }, [])

  return (
    <div className={stylesVideo.articleVideo}>
      <WriteUs toggleFeedback={toggleFeedback} setToggleFeedback={handleToggleFeedback} />
      <div className={styles.articleWrapper}>
        <div
          className={clsx(stylesVideo.articleTopVideo, {
            [stylesVideo.wrapperWithFormat]: isWithFormat,
          })}
        >
          {isWithFormat && (
            <section
              className={clsx(styles.recomendationSection, styles.recomendationSectionVertical)}
            >
              <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
                <div>{children}</div>
              </WidthLimiter>
            </section>
          )}
          <WidthLimiter noTopPadding noBottomPadding noLeftPadding noRightPadding>
            <div className={stylesVideo.whiteBgVideo}>
              <main>
                <div className={stylesVideo.breadcrumb}>
                  <BreadcrumbsPage
                    breadData={[
                      {
                        name: isWebinar ? 'Вебинары' : 'Библиотека',
                        link: isWebinar ? `${URL_SITE}/webinar` : `${URL_SITE}/library`,
                      },
                      { name: dataset.name, link: '' },
                    ]}
                  />
                </div>

                <VideoBlockProject {...props} isFullInShow={isFullInShow} />
                <>
                  {!isBigPC && (
                    <div className={styles.toggleWrap}>
                      <SwitchToggle
                        options={OPTIONS}
                        selectedOption={selectedOption}
                        handleToggle={setSelectedOption}
                        modificator={stylesVideo.switch}
                        wrapperModificator={stylesVideo.switchWrap}
                        isWithoutFade
                      />
                    </div>
                  )}

                  {!isFullInShow && <Playlist data={playlist} />}
                </>
              </main>
            </div>

            {!dataset.isHideComments && (
              <div className={styles.commentariesTop}>
                <Commentaries
                  comments={dataset.Comments}
                  id={dataset.id}
                  materialType={isWebinar ? 'webinar' : 'article'}
                  isWithNMO={Boolean(dataset.activeNMOCodes)}
                  isAllowComment={dataset?.isAcceptAllow}
                />
                <div className={styles.writeUsBotom}>
                  <h3 className={styles.writeUsTitle}>Остались вопросы?</h3>
                  <WriteUsBtn handler={handleToggleFeedback} />
                </div>
              </div>
            )}
          </WidthLimiter>
          {isBigPC && (
            <div className={stylesVideo.playlistWrap}>
              <div className={stylesVideo.playlistHeader}>
                <p className={stylesVideo.playlistTitle}>Следующие видео</p>
                {isLoaded && (
                  <p className={stylesVideo.playlistCount}>{`${currWebinarIndex + 1} из ${
                    playlist.length
                  } видео`}</p>
                )}
              </div>
              {isLoaded ? (
                <Playlist data={playlist} modificator={stylesVideo.scrollWrap} />
              ) : (
                <div className={stylesVideo.loader}>
                  <Loader />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {isFullInShow && (
        <div className={styles.commentariesBottom}>
          <WidthLimiter
            noTopPadding
            noBottomPadding
            noLeftPadding
            noRightPadding
            modificator={styles.widthLimMod}
          >
            {!dataset.isHideComments && (
              <Commentaries
                comments={dataset.Comments}
                id={dataset.id}
                materialType={isWebinar ? 'webinar' : 'article'}
                isWithNMO={Boolean(dataset.activeNMOCodes)}
                isAllowComment={dataset?.isAcceptAllow}
              />
            )}
            <div className={styles.writeUsBotom}>
              <h3 className={styles.writeUsTitle}>Остались вопросы?</h3>
              <WriteUsBtn handler={handleToggleFeedback} />
            </div>
          </WidthLimiter>
        </div>
      )}
    </div>
  )
}

export default ArticleVideoWithPlaylist
